import React from 'react'
import { Place, WithContext, Organization, Event } from 'schema-dts'
import { SGLinkedData } from '@scentregroup/shared/hub-components/json-ld'
import { fullCentreTitle } from '@scentregroup/shared/helpers/full-centre-title'
import { setCacheHeaders } from '@scentregroup/shared/helpers/set-cache-headers'
import {
  useGetHrefContext,
  TimeZoneContext,
} from '@scentregroup/shared/context'
import Head from 'next/head'
import {
  CentreStaticData,
  fetchCentreStaticData,
} from '@scentregroup/shared/hub-components/universal-header'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import { getDefaultCentreMetadata } from '@scentregroup/shared/helpers/get-default-centre-metadata'
import Layout from '@scentregroup/shared/hub-components/layout'
import {
  getCentreDetails,
  CentreDetails,
  buildContactLinks,
} from '@scentregroup/shared/helpers/centre'
import { DateTime } from '@scentregroup/shared/helpers/date-helpers'
import { TradingHourDay } from '@scentregroup/shared/graphql'
import { useStoredCentre } from '@scentregroup/shared/hooks/use-stored-centre'
import { completelyCanonicalUrl } from '@scentregroup/shared/helpers/canonical-link'
import {
  GetServerSidePropsContext,
  GetServerSidePropsResult,
} from '../../../../types/next'
import { makeApolloClient } from '../../../../lib/apolloClient'
import { OpenGraph } from '../../../../lib/open-graph'
import { SGPageTemplateWithAdZones } from '@scentregroup/shared/types/page-templates'
import {
  ContentDetail,
  getContentDetail,
} from '../../../../lib/get-content-detail'
import { EventContentDetail } from '../../../../components/page-templates/content-detail/event'
import { getCentrePageTemplate } from '../../../../lib/page-templates/fetching'
import {
  getTradingHours,
  CentreSlug,
  StoreId,
  DateString,
} from '../../../../lib/get-trading-hours'
import { shouldRedirectExpiredCentreContent } from '../../../../lib/redirect-expired-content'
import {
  getTopStripProps,
  ITopStripProps,
} from '@scentregroup/shared/hub-components/top-strip'
import Breadcrumbs from '@scentregroup/shared/hub-components/breadcrumbs'
import { BreadcrumbContainer } from '../../../../components/breadcrumb-container'
import { shouldRedirectForMismatchedRoute } from '@scentregroup/shared/lib/redirects'

export interface ICentreEventPageProps {
  country: ReturnType<typeof localeToCountry>
  topStripProps: ITopStripProps | null
  centre: CentreDetails
  content: ContentDetail['event']
  template: SGPageTemplateWithAdZones
  headerStaticData: CentreStaticData
  tradingHours: TradingHourDay[]
}

export async function getServerSideProps(
  ctx: GetServerSidePropsContext<{
    centre: string
    id: string
    slug: string
  }>
): Promise<GetServerSidePropsResult<ICentreEventPageProps>> {
  setCacheHeaders(ctx.res)
  const country = localeToCountry(ctx.locale)

  const centreSlug = ctx.params?.centre
  const eventId = ctx.params?.id
  if (!centreSlug || !eventId) {
    return {
      notFound: true,
    }
  }

  const apollo = makeApolloClient()
  const centre = await getCentreDetails(apollo, { slug: centreSlug, country })
  if (!centre) {
    return {
      notFound: true,
    }
  }
  const redirectForCentreSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/event/[id]/[slug]',
    ctx.params,
    {
      centre: centre.slug,
    }
  )
  if (redirectForCentreSlug) {
    return redirectForCentreSlug
  }
  const [template, content, headerStaticData, topStripProps] =
    await Promise.all([
      getCentrePageTemplate(apollo, centreSlug, 'event'),
      getContentDetail(apollo, 'event', { centreSlug, id: eventId }),
      fetchCentreStaticData(apollo, {
        country: localeToCountry(ctx.locale),
        centre,
      }),
      getTopStripProps(apollo, country, centreSlug),
    ])

  if (!content || !template) {
    return {
      notFound: true,
    }
  }

  const redirectForEventSlug = shouldRedirectForMismatchedRoute(
    '/[centre]/event/[id]/[slug]',
    ctx.params,
    {
      slug: content.slug,
    }
  )

  if (redirectForEventSlug) {
    return redirectForEventSlug
  }

  const redirect = shouldRedirectExpiredCentreContent(
    content.hideAt,
    `/${centreSlug}`
  )

  if (redirect) {
    return redirect
  }

  const startDate = DateTime.moment()
    .tz(centre.timeZone)
    .startOf('day')
    .toString() as DateString

  const storeId = content?.storesCollection?.featuredStoreForCentre?.sys.id
  const tradingHours = await getTradingHours(
    apollo,
    centreSlug as CentreSlug,
    storeId as StoreId,
    startDate
  )

  return {
    props: {
      country,
      topStripProps,
      centre,
      content,
      template,
      headerStaticData,
      tradingHours,
    },
  }
}

export default function EventOfferPage({
  country,
  topStripProps,
  centre,
  content,
  template,
  headerStaticData,
  tradingHours,
}: ICentreEventPageProps): JSX.Element {
  const canonical = completelyCanonicalUrl(useGetHrefContext()())
  const meta = getDefaultCentreMetadata({
    centre,
    canonical,
    title: `${content.title} at Westfield ${centre.title}`,
    description: content.shortDescription,
    imageUrl: content.heroImageSet?.master?.url,
  })

  const metaData = {
    title: `${content.title} at Westfield ${centre.title}`,
    description: `Westfield ${centre.title} ${template.title} - Check opening hours, phone number, address & directions. Find 100s of quality retailers like ${template.title} & more at Westfield ${centre.title}!`,
  }
  const contactLinks = buildContactLinks(centre)
  useStoredCentre(centre)

  const ldEvent: WithContext<Event> = useLDEvent(content, centre, canonical)

  return (
    <TimeZoneContext.Provider value={centre.timeZone}>
      <SGLinkedData item={meta.schema} />
      <SGLinkedData item={ldEvent} />
      <OpenGraph data={meta.openGraphData} />
      <Head>
        <title>{metaData.title}</title>
        <meta
          key="description"
          name="description"
          content={metaData.description}
        />
        <link key="canonical" rel="canonical" href={canonical} />
      </Head>
      <Layout
        country={country}
        topStrip={topStripProps}
        centre={{
          ...centre,
          contactLinks,
        }}
        header={{
          variant: 'centre',
          data: headerStaticData,
          hasBottomMargin: [false, false, true],
          logoText: centre.title,
        }}
        metaData={metaData}
      >
        <BreadcrumbContainer>
          <Breadcrumbs
            breadcrumbs={[
              { title: centre.title, linkTo: `/${centre.slug}` },
              { title: "What's happening", linkTo: `/${centre.slug}/event` },
              { title: meta.title },
            ]}
          />
        </BreadcrumbContainer>
        <EventContentDetail
          centre={{
            ...centre,
            contactLinks,
          }}
          content={content}
          template={template}
          tradingHours={tradingHours}
        />
      </Layout>
    </TimeZoneContext.Provider>
  )
}

function useLDEvent(
  content: ContentDetail['event'],
  centre: CentreDetails,
  canonical: string
): WithContext<Event> {
  const ldEventLocation = React.useMemo<Place>(
    () =>
      content.storesCollection?.featuredStoreForCentre?.nearestLocation &&
      content.storesCollection?.featuredStoreForCentre?.physicalAddress
        ? {
            '@type': 'Place',
            name: fullCentreTitle(centre),
            address: {
              '@type': 'PostalAddress',
              streetAddress: `${content.storesCollection.featuredStoreForCentre.nearestLocation}, ${content.storesCollection.featuredStoreForCentre.physicalAddress}, ${centre.physicalAddress}`,
              addressLocality: centre.suburb,
              postalCode: centre.postcode,
              addressRegion: centre.state,
              addressCountry: centre.country,
            },
          }
        : {
            '@type': 'Place',
            name: fullCentreTitle(centre),
            address: {
              '@type': 'PostalAddress',
              streetAddress: centre.physicalAddress,
              addressLocality: centre.suburb,
              postalCode: centre.postcode,
              addressRegion: centre.state,
              addressCountry: centre.country,
            },
          },
    [
      centre,
      content.storesCollection?.featuredStoreForCentre?.nearestLocation,
      content.storesCollection?.featuredStoreForCentre?.physicalAddress,
    ]
  )

  const ldEventOrganizer = React.useMemo<Organization>(
    () =>
      content.storesCollection?.featuredStoreForCentre?.storeTitle &&
      content.storesCollection?.featuredStoreForCentre?.websiteAddress
        ? {
            '@type': 'Organization',
            name: content.storesCollection.featuredStoreForCentre.storeTitle,
            url: content.storesCollection.featuredStoreForCentre.websiteAddress,
          }
        : {
            '@type': 'Organization',
            name: fullCentreTitle(centre),
            url: canonical,
          },
    [
      content.storesCollection?.featuredStoreForCentre?.storeTitle,
      content.storesCollection?.featuredStoreForCentre?.websiteAddress,
      centre,
      canonical,
    ]
  )

  const ldEvent = React.useMemo<WithContext<Event>>(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'Event',
      '@id': `${canonical}#event`,
      eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
      eventStatus: 'https://schema.org/EventScheduled',
      name: content.title,
      url: canonical,
      startDate: content.startAt,
      endDate: content.endAt,
      location: ldEventLocation,
      image: content.heroImageSet?.master?.url ?? undefined,
      description: content.shortDescription ?? undefined,
      organizer: ldEventOrganizer,
    }),
    [
      content.title,
      canonical,
      content.startAt,
      content.endAt,
      ldEventLocation,
      content.heroImageSet?.master?.url,
      content.shortDescription,
      ldEventOrganizer,
    ]
  )

  return ldEvent
}
